/* eslint @typescript-eslint/strict-boolean-expressions: 0, @typescript-eslint/no-floating-promises: 0 */
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Tab as MuiTab,
  Select,
  type SelectChangeEvent,
  styled,
  Tabs,
  Typography
} from '@mui/material'
import { type ConfigProps } from 'components/Dashboard'
import {
  DateInput,
  type ScrollProps,
  ScrollTop,
  useNotify
} from 'components/Util'
import {
  addMonths,
  eachMonthOfInterval,
  format,
  getYear,
  isAfter,
  isBefore,
  parseISO,
  startOfMonth,
  subDays,
  subYears
} from 'date-fns/fp'
import {
  compact,
  concat,
  filter,
  find,
  findIndex,
  flatten,
  groupBy,
  head,
  includes,
  isArray,
  isEmpty,
  isNaN,
  isNil,
  isNumber,
  keys,
  last,
  map,
  range,
  reduce,
  replace,
  reverse,
  size,
  slice,
  sortBy,
  split,
  startsWith,
  toNumber
} from 'lodash/fp'
import React, { type ReactElement } from 'react'
import {
  Form,
  type Identifier,
  Loading,
  type RaRecord,
  required,
  type RowClickFunction,
  useGetIdentity,
  usePermissions,
  useTranslate
} from 'react-admin'
import SquareIcon from '@mui/icons-material/Square'
import {
  useGetElectricityDemandMonthly,
  useGetElectricityYearly,
  useGetMappings
} from 'components/DataProvider'
import {
  ElectricityChartYearlyKind,
  type Mapping,
  type ElectricityYearly
} from '../../../../cdk/bapp/lib/lambda-api-v2/src/types'
import {
  createDashboardAllChart,
  createDashboardOrganizationChart,
  // createDashboardSecondChart,
  createDemandMonthlyChart
} from 'components/Chart/chartjs'
import { type ChartConfiguration } from 'chart.js'
import { SolarChart } from 'components/Chart'
import log from 'loglevel'
import { type DateView } from '@mui/x-date-pickers'
import {
  ArrowLeft,
  ArrowRight,
  ArrowDropDown,
  ArrowDropUp
} from '@mui/icons-material'
import { SpotSelect } from 'components/SpotSelect'
import { Compare } from 'components/Compare'
import JSZip from 'jszip'
import { json2xlsx } from '@mzc-watchover/json2xlsx'
import { DrawerContext } from 'components/Admin'

log.setLevel(log.levels.DEBUG)

const Tab = styled((props: any) => <MuiTab {...props} />)(() => ({
  '&.Mui-selected': {
    background: '#F9DD87',
    fontWeight: 'bold'
  }
}))

const DashboardControl: React.FC<
  {
    date: boolean
    index?: number | null | undefined
    spot?: boolean | null | undefined
    // first?: boolean
    // second?: boolean
    partner: string
    targetDate: string
    target?: string | null | undefined
    setTargetDate: (date: string) => void
    setTarget: (target: string) => void
    noDownload?: boolean
    data?: any
  } & ConfigProps
> = ({
  date,
  index,
  spot,
  // first,
  // second,
  partner,
  config,
  targetDate,
  target,
  setTargetDate,
  setTarget,
  noDownload,
  data
}) => {
  const { maxDate, Limits: configLimits, Mappings: configMappings } = config
  const { organizations: organizationKeys } = configMappings
  const translate = useTranslate()
  const dateViews: DateView[] = ['year', 'month']
  const { start } = configLimits ?? { start: undefined }
  const handleRowClick: RowClickFunction = (
    id: Identifier,
    _resource: string,
    _record: RaRecord
  ): any => {
    setTarget(id as string)
    return false
  }
  const handleDownload = (): void => {
    if (isNil(data)) return
    const url = URL.createObjectURL(data)
    const download = document.createElement('a')
    download.href = url
    download.download = `${format('yyyyMM')(parseISO(targetDate))}${
      !isNil(target) ? (target as string) : ''
    }ダッシュボード_${format('yyyyMMdd')(Date.now())}.zip`
    download.click()
    window.URL.revokeObjectURL(url)
  }
  const { organizations } = config.Mappings
  return (
    <Box
      sx={{
        p: '0.5em',
        bgcolor: '#F9DD87',
        display: 'flex',
        flexDirection: window.innerWidth <= 900 ? 'column' : 'row',
        gap: window.innerWidth <= 900 ? 1 : 2,
        alignItems: window.innerWidth <= 900 ? 'flex-start' : 'center'
      }}>
      {(date as boolean) && (
        <Form
          defaultValues={{
            date: targetDate
          }}>
          <DateInput
            source="date"
            label={translate('resources.spots.fields.date')}
            validate={required()}
            disableFuture
            minDate={!isNil(start) ? parseISO(start) : undefined}
            maxDate={
              !isNil(maxDate)
                ? parseISO(maxDate)
                : isAfter(
                    parseISO(`${format('yyyy-MM-dd')(Date.now())}T15:00`)
                  )(Date.now())
                ? subDays(1)(Date.now())
                : subDays(2)(Date.now())
            }
            views={dateViews}
            onChange={(_source: string, value: string): void => {
              setTargetDate(
                includes('day')(dateViews)
                  ? value
                  : format('yyyy-MM-dd')(startOfMonth(parseISO(value)))
              )
            }}
            withControl
          />
        </Form>
      )}
      {!isNil(index) && !isNil(organizations[index]) && (
        <OrganizationSelect
          partner={partner}
          setTarget={setTarget}
          organizationKeys={organizations}
          index={index}
          filterKeys={organizationKeys}
        />
      )}
      {!isNil(spot) && (spot as boolean) && (
        <SpotSelect
          rowClick={handleRowClick}
          opened={isNil(target)}
          config={config}
          target={target}
          kind="高圧"
        />
      )}
      {/* {(first as boolean) && (
        <FirstSelect
          partner={partner}
          setTarget={setTarget}
          areaKey={organizations[0]}
        />
      )}
      {(second as boolean) && (
        <SpotSelect
          rowClick={handleRowClick}
          opened={isNil(target)}
          config={config}
          target={target}
          kind="高圧"
        />
      )} */}
      {!(noDownload ?? false) && (
        <Button variant="contained" onClick={handleDownload}>
          {translate('resources.dashboard2.buttons.download')}
        </Button>
      )}
    </Box>
  )
}
const AllTable: React.FC<{
  date: string
  values: number[]
  lastValues: number[]
  compareValues: number[]
  header: string[]
  setTableData: (data: Uint8Array) => void
}> = ({
  values: valuesArg,
  lastValues: lastValuesArg,
  compareValues: compareValuesArg,
  header: headerArg,
  date,
  setTableData
}) => {
  const [values, setValues] = React.useState<Array<number | string>>([])
  const [lastValues, setLastValues] = React.useState<Array<number | string>>([])
  const [compareValues, setCompareValues] = React.useState<
    Array<number | string>
  >([])
  const [header, setHeader] = React.useState<string[]>([])
  React.useEffect(() => {
    const values = [
      `${format('y年度')(parseISO(date))}(kWh)`,
      reduce((sum: number, value: number) => sum + value, 0)(valuesArg),
      ...valuesArg
    ]
    const lastValues = [
      `${format('y年度')(subYears(1)(parseISO(date)))}(kWh)`,
      reduce((sum: number, value: number) => sum + value, 0)(lastValuesArg),
      ...lastValuesArg
    ]
    const compareValues = [
      '前年度比',
      Math.floor(((values[1] as number) / (lastValues[1] as number)) * 100),
      ...compareValuesArg
    ]
    const header = ['使用量', '合計', ...headerArg]
    setValues(values)
    setLastValues(lastValues)
    setCompareValues(compareValues)
    setHeader(header)
    const data = map((items: Array<string | number>) =>
      reduce(
        (
          sum: Record<string, string | number>,
          item: Record<string, string | number>
        ) => ({ ...sum, ...item }),
        {}
      )(
        // @ts-expect-error ts(2339)
        map.convert({ cap: false })((key: string, index: number) => ({
          [key]: items[index]
        }))(header)
      )
    )([values, lastValues, compareValues])
    json2xlsx(data).then((xlsx) => {
      if (!isNil(xlsx)) setTableData(xlsx)
    })
  }, [date, valuesArg, lastValuesArg, compareValuesArg, headerArg])
  return (
    <Box
      sx={{
        width: '100%',
        overflow: 'scroll'
      }}>
      <Box sx={{ display: 'flex', mb: ' 1em' }}>
        <Box sx={{ display: 'flex' }}>
          {
            // @ts-expect-error ts(2339)
            map.convert({ cap: false })((_value: number, index: number) => (
              <Box
                sx={{
                  width: index === 0 ? '12em' : '8em',
                  fontSize: window.innerWidth < 1200 ? 9 : 14.5
                }}
                key={index}>
                <Box
                  sx={{
                    border: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    p: '0.5em'
                  }}>
                  {header[index]}
                </Box>
                <Box
                  sx={{
                    border: 1,
                    display: 'flex',
                    justifyContent: index === 0 ? 'center' : 'flex-end',
                    p: '0.5em'
                  }}>
                  {lastValues[index]?.toLocaleString()}
                </Box>
                <Box
                  sx={{
                    border: 1,
                    display: 'flex',
                    justifyContent: index === 0 ? 'center' : 'flex-end',
                    p: '0.5em'
                  }}>
                  {isNil(values[index]) ? '-' : values[index].toLocaleString()}
                </Box>
                <Box
                  sx={{
                    border: 1,
                    display: 'flex',
                    justifyContent: index === 0 ? 'center' : 'flex-end',
                    p: '0.5em',
                    backgroundColor:
                      !isNil(compareValues[index]) &&
                      isNumber(compareValues[index]) &&
                      compareValues[index] < 100
                        ? '#CFE1F5'
                        : 'white'
                  }}>
                  {compareValues[index] ?? '-'}
                  {index === 0 || isNil(compareValues[index]) ? '' : '%'}
                </Box>
              </Box>
            ))(lastValues)
          }
        </Box>
      </Box>
    </Box>
  )
}
const OrganizationTable: React.FC<{
  date: string
  values: number[]
  lastValues: number[]
  compareValues: Array<number | string>
  header: string[]
  setTableData: (data: Uint8Array) => void
}> = ({
  values: valuesArg,
  lastValues: lastValuesArg,
  compareValues: compareValuesArg,
  header: headerArg,
  date,
  setTableData
}) => {
  const [values, setValues] = React.useState<Array<number | string>>([])
  const [lastValues, setLastValues] = React.useState<Array<number | string>>([])
  const [compareValues, setCompareValues] = React.useState<
    Array<number | string>
  >([])
  const [header, setHeader] = React.useState<string[]>([])
  React.useEffect(() => {
    const values = [format('y年M月')(parseISO(date)), ...valuesArg]
    const lastValues = [
      format('y年M月')(subYears(1)(parseISO(date))),
      ...lastValuesArg
    ]
    const compareValues = ['前年比（%）', ...compareValuesArg]
    const header = ['kWh', ...headerArg]
    setValues(values)
    setLastValues(lastValues)
    setCompareValues(compareValues)
    setHeader(header)
    const data = map((items: Array<string | number>) =>
      reduce(
        (
          sum: Record<string, string | number>,
          item: Record<string, string | number>
        ) => ({ ...sum, ...item }),
        {}
      )(
        // @ts-expect-error ts(2339)
        map.convert({ cap: false })((key: string, index: number) => ({
          [key]: items[index]
        }))(header)
      )
    )([values, lastValues, compareValues])
    json2xlsx(data).then((xlsx) => {
      if (!isNil(xlsx)) setTableData(xlsx)
    })
  }, [date, valuesArg, lastValuesArg, compareValuesArg, headerArg])
  return (
    <Box sx={{ width: '100%', overflow: 'scroll' }}>
      <Box sx={{ display: 'flex', mb: ' 1em' }}>
        <Box sx={{ display: 'flex' }}>
          {
            // @ts-expect-error ts(2339)
            map.convert({ cap: false })((_value: number, index: number) => (
              <Box
                sx={{
                  width: index === 0 ? '12em' : '10em',
                  fontSize: window.innerWidth < 1200 ? 9 : 14.5
                }}
                key={index}>
                <Box
                  sx={{
                    border: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    p: '0.5em'
                  }}>
                  {header[index]}
                </Box>
                <Box
                  sx={{
                    border: 1,
                    display: 'flex',
                    justifyContent: index === 0 ? 'center' : 'flex-end',
                    p: '0.5em'
                  }}>
                  {lastValues[index]?.toLocaleString()}
                </Box>
                <Box
                  sx={{
                    border: 1,
                    display: 'flex',
                    justifyContent: index === 0 ? 'center' : 'flex-end',
                    p: '0.5em'
                  }}>
                  {isNil(values[index]) ? '-' : values[index].toLocaleString()}
                </Box>
                <Box
                  sx={{
                    border: 1,
                    display: 'flex',
                    justifyContent: index === 0 ? 'center' : 'flex-end',
                    p: '0.5em',
                    backgroundColor:
                      !isNil(compareValues[index]) &&
                      isNumber(compareValues[index]) &&
                      compareValues[index] < 100
                        ? '#CFE1F5'
                        : 'white'
                  }}>
                  {compareValues[index] ?? '-'}
                  {index === 0 || isNil(compareValues[index]) ? '' : '%'}
                </Box>
              </Box>
            ))(lastValues)
          }
        </Box>
      </Box>
    </Box>
  )
}
const PlaceTable: React.FC<{
  date: string
  values: number[]
  lastValues: number[]
  compareValues: Array<number | string>
  header: string[]
  ids: string[]
  setTableData: (data: Uint8Array) => void
}> = ({
  values: valuesArg,
  lastValues: lastValuesArg,
  compareValues: compareValuesArg,
  header: headerArg,
  date,
  ids,
  setTableData
}) => {
  const [values, setValues] = React.useState<Array<number | string>>([])
  const [lastValues, setLastValues] = React.useState<Array<number | string>>([])
  const [compareValues, setCompareValues] = React.useState<
    Array<number | string>
  >([])
  const [header, setHeader] = React.useState<string[]>([])

  const { pathname } = window.location
  React.useEffect(() => {
    const values = [format('y年M月')(parseISO(date)), ...valuesArg]
    const lastValues = [
      format('y年M月')(subYears(1)(parseISO(date))),
      ...lastValuesArg
    ]
    const compareValues = ['前年比（%）', ...compareValuesArg]
    const header = ['kWh', ...headerArg]
    setValues(values)
    setLastValues(lastValues)
    setCompareValues(compareValues)
    setHeader(header)
    const data = map((items: Array<string | number>) =>
      reduce(
        (
          sum: Record<string, string | number>,
          item: Record<string, string | number>
        ) => ({ ...sum, ...item }),
        {}
      )(
        // @ts-expect-error ts(2339)
        map.convert({ cap: false })((key: string, index: number) => ({
          [key]: items[index]
        }))(header)
      )
    )([values, lastValues, compareValues])
    json2xlsx(data).then((xlsx) => {
      if (!isNil(xlsx)) setTableData(xlsx)
    })
  }, [date, valuesArg, lastValuesArg, compareValuesArg, headerArg])
  return (
    <Box sx={{ width: '100%', overflow: 'scroll' }}>
      <Box sx={{ display: 'flex', mb: ' 1em' }}>
        <Box sx={{ display: 'flex' }}>
          {
            // @ts-expect-error ts(2339)
            map.convert({ cap: false })((_value: number, index: number) => (
              <Box
                sx={{
                  width: index === 0 ? '12em' : '4em',
                  fontSize: window.innerWidth < 1200 ? 9 : 14.5
                }}
                key={index}>
                <Box
                  sx={{
                    border: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    p: '0.5em'
                  }}>
                  {index > 0 ? (
                    <a
                      href={`${replace('/dashboard', '')(pathname)}/spot/${
                        ids[index - 1] as string
                      }`}
                      target="_blank">
                      {header[index]}
                    </a>
                  ) : (
                    header[index]
                  )}
                </Box>
                <Box
                  sx={{
                    border: 1,
                    display: 'flex',
                    justifyContent: index === 0 ? 'center' : 'flex-end',
                    p: '0.5em'
                  }}>
                  {lastValues[index]?.toLocaleString()}
                </Box>
                <Box
                  sx={{
                    border: 1,
                    display: 'flex',
                    justifyContent: index === 0 ? 'center' : 'flex-end',
                    p: '0.5em'
                  }}>
                  {isNil(values[index]) ? '-' : values[index].toLocaleString()}
                </Box>
                <Box
                  sx={{
                    border: 1,
                    display: 'flex',
                    justifyContent: index === 0 ? 'center' : 'flex-end',
                    p: '0.5em',
                    backgroundColor:
                      !isNil(compareValues[index]) &&
                      isNumber(compareValues[index]) &&
                      compareValues[index] < 100
                        ? '#CFE1F5'
                        : 'white'
                  }}>
                  {isNaN(compareValues[index] as number)
                    ? '-'
                    : compareValues[index] ?? '-'}
                  {index === 0 ||
                  isNaN(compareValues[index] as number) ||
                  isNil(compareValues[index])
                    ? ''
                    : '%'}
                </Box>
              </Box>
            ))(lastValues)
          }
        </Box>
      </Box>
    </Box>
  )
}
const RankingTable: React.FC<
  {
    values: number[]
    lastValues: number[]
    compareValues: Array<number | string>
    names: Array<{
      name: string
      area: string
      id: string
      floorArea: number | null | undefined
    }>
    setTableData: (data: Uint8Array) => void
    area: string | null | undefined
  } & ConfigProps &
    ScrollProps
> = ({
  values,
  lastValues,
  compareValues,
  names,
  setTableData,
  area,
  config,
  scrollRef
}) => {
  const [data, setData] = React.useState<any>(undefined)
  const { organizations, name: nameKey } = config.Mappings
  React.useEffect(() => {
    // @ts-expect-error ts(2339)
    const data = map.convert({ cap: false })((item: string, index: number) => {
      const { floorArea } = names[index] ?? { floorArea: undefined }
      const value = toNumber(item)
      return {
        ranking: index + 1,
        id: names[index]?.id,
        name: names[index]?.name,
        area: names[index]?.area,
        value: item,
        // lastValue: lastValues[index],
        valuePerFloor: !isNil(floorArea)
          ? Math.floor((value * 10) / floorArea) / 10
          : item,
        compareValue: compareValues[index]
      }
    })(slice(0, size(values))(values))
    setData(data)
  }, [values, lastValues, compareValues, names])
  React.useEffect(() => {
    json2xlsx(data, undefined, {
      headers: {
        ranking: 'ランキング',
        name: `${nameKey as string}名`,
        value: '電力量',
        valuePerFloor: '電力量／m2',
        compareValue: '対前年比',
        area: organizations[0]
      },
      order: [
        'ranking',
        'name',
        'area',
        'compareValue',
        'value',
        'valuePerFloor'
      ]
    }).then((xlsx) => {
      if (!isNil(xlsx)) setTableData(xlsx)
      if (!isNil(scrollRef) && !isNil(scrollRef.current)) {
        scrollRef.current.scrollIntoView()
      }
    })
  }, [data])
  const handleSort = (key: string, order: string) => (): void => {
    const values = sortBy<any>((item: any) =>
      item[key] === '-' ? -1 : item[key]
    )(data)
    setData(
      // @ts-expect-error ts(2339)
      map.convert({ cap: false })((item: any, index: number) => ({
        ...item,
        ranking: index + 1
      }))(order === 'desc' ? reverse(values) : values)
    )
  }
  if (isNil(data)) return null
  const fontSize = 20
  const headerFontSize = window.innerWidth < 1300 ? 16 : 20
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        height: `${Math.floor(window.innerHeight * 0.9)}px`
      }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            overflowY: 'scroll',
            height: '250px',
            width: `${window.innerWidth * 0.9}px`
          }}>
          <Box
            sx={{
              width: `${window.innerWidth * 0.12}px`,
              border: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: '0.5em',
              backgroundColor: '#FEF7E3',
              fontSize: headerFontSize
            }}>
            ランキング
          </Box>
          <Box
            sx={{
              width: `${window.innerWidth * 0.18}px`,
              border: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: '0.5em',
              backgroundColor: '#FEF7E3',
              fontSize: headerFontSize
            }}>
            {`${nameKey as string}名`}
          </Box>
          <Box
            sx={{
              width: `${window.innerWidth * 0.18}px`,
              border: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: '0.5em',
              backgroundColor: '#FEF7E3',
              fontSize: headerFontSize
            }}>
            {organizations[0]}
            {isNil(area) && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: '#FEF7E3'
                }}>
                <IconButton onClick={handleSort('area', 'asc')}>
                  <ArrowDropUp />
                </IconButton>
                <IconButton onClick={handleSort('area', 'desc')}>
                  <ArrowDropDown />
                </IconButton>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              width: `${window.innerWidth * 0.42}px`,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#FEF7E3',
              fontSize: headerFontSize
            }}>
            <Box
              sx={{
                display: 'flex',
                border: 1,
                justifyContent: 'center',
                alignItems: 'center',
                p: '0.5em',
                height: '50px'
              }}>
              電力量
            </Box>
            <Box
              sx={{ display: 'flex', flexDirection: 'row', height: '200px' }}>
              <Box
                sx={{
                  width: `${100 / 3}%`,
                  border: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: '0.5em'
                }}>
                対前年比
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <IconButton onClick={handleSort('compareValue', 'asc')}>
                    <ArrowDropUp />
                  </IconButton>
                  <IconButton onClick={handleSort('compareValue', 'desc')}>
                    <ArrowDropDown />
                  </IconButton>
                </Box>
              </Box>
              <Box
                sx={{
                  width: `${100 / 3}%`,
                  border: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: '0.5em'
                }}>
                電力量
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <IconButton onClick={handleSort('value', 'asc')}>
                    <ArrowDropUp />
                  </IconButton>
                  <IconButton onClick={handleSort('value', 'desc')}>
                    <ArrowDropDown />
                  </IconButton>
                </Box>
              </Box>
              <Box
                sx={{
                  width: `${100 / 3}%`,
                  border: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: '0.5em'
                }}>
                電力量／m2
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <IconButton onClick={handleSort('unitValue', 'asc')}>
                    <ArrowDropUp />
                  </IconButton>
                  <IconButton onClick={handleSort('unitValue', 'desc')}>
                    <ArrowDropDown />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            height: '1000px',
            overflow: 'scroll',
            width: `${window.innerWidth * 0.9}px`
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'white'
            }}>
            {map((item: any) => {
              const { ranking, name, id, area, value, compareValue } = item
              return (
                <Box sx={{ display: 'flex' }} key={id}>
                  <Box
                    sx={{
                      width: `${window.innerWidth * 0.12}px`,
                      border: 1,
                      display: 'flex',
                      justifyContent: 'flex-end',
                      p: '0.5em',
                      fontSize
                    }}>
                    {ranking.toLocaleString()}
                  </Box>
                  <Box
                    sx={{
                      width: `${window.innerWidth * 0.18}px`,
                      border: 1,
                      display: 'flex',
                      justifyContent: 'center',
                      p: '0.5em',
                      fontSize
                    }}>
                    <a href={`spot/${id as string}`}>{name}</a>
                  </Box>
                  <Box
                    sx={{
                      width: `${window.innerWidth * 0.18}px`,
                      border: 1,
                      display: 'flex',
                      justifyContent: 'center',
                      p: '0.5em',
                      fontSize
                    }}>
                    {area}
                  </Box>
                  <Box
                    sx={{
                      width: `${window.innerWidth * 0.42}px`,
                      display: 'flex',
                      flexDirection: 'row'
                    }}>
                    <Box
                      sx={{
                        width: `${100 / 3}%`,
                        border: 1,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        p: '0.5em',
                        fontSize
                      }}>
                      <Typography
                        variant="inherit"
                        sx={{
                          color: isNumber(compareValue)
                            ? compareValue < 100
                              ? 'blue'
                              : 'red'
                            : 'black',
                          fontSize
                        }}>
                        {compareValue === '-'
                          ? (compareValue as string)
                          : `${compareValue.toLocaleString() as string}%`}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: `${100 / 3}%`,
                        border: 1,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        p: '0.5em',
                        fontSize
                      }}>
                      {value.toLocaleString()}
                    </Box>
                    <Box
                      sx={{
                        width: `${100 / 3}%`,
                        border: 1,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        p: '0.5em',
                        fontSize
                      }}>
                      {value.toLocaleString()}
                    </Box>
                  </Box>
                </Box>
              )
            })(data)}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
const All: React.FC<
  { date: string; setData: (data: any) => void } & ConfigProps & ScrollProps
> = ({ date, config, setData, scrollRef }) => {
  const notify = useNotify()
  const { Chart: configChart } = config
  const { data: rawData, isLoading } = useGetElectricityYearly(
    date,
    ElectricityChartYearlyKind.lastYear
  )
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const [values, setValues] = React.useState<number[]>([])
  const [lastValues, setLastValues] = React.useState<number[]>([])
  const [compareValues, setCompareValues] = React.useState<number[]>([])
  const [header, setHeader] = React.useState<string[]>([])
  const [tableData, setTableData] = React.useState<Uint8Array | undefined>(
    undefined
  )
  const [chartData, setChartData] = React.useState<any | undefined>(undefined)
  const reduceData = (rawData: ElectricityYearly[]): number[] => {
    const data = groupBy<{ date: string; daily: number | null }>('date')(
      flatten(map((item: ElectricityYearly) => item.amounts)(rawData))
    )
    return compact(
      map((key: string) =>
        key === 'undefined'
          ? undefined
          : Math.floor(
              reduce(
                (sum: number, item: { daily: number | null }) =>
                  sum + (item.daily ?? 0),
                0
              )(data[key])
            )
      )(sortBy<string>((value) => value)(keys(data)))
    )
  }
  React.useEffect(() => {
    if (isLoading) {
      notify('loading', { autoHideDuration: null })
    } else {
      notify('loading', { autoHideDuration: 0 })
    }
    if (!isNil(rawData) && isNil(chart)) {
      const values = reduceData(compact(rawData.data))
      const lastValues = reduceData(compact(rawData.compareData))
      const compareValues = // @ts-expect-error ts(2339)
        map.convert({ cap: false })((value: number | null, index: number) =>
          isNil(lastValues[index])
            ? null
            : Math.floor(((value ?? 0) / lastValues[index]) * 100)
        )(values)
      const header = map((date: Date) => format('M月')(date))(
        eachMonthOfInterval({
          start: parseISO(date),
          end: addMonths(11)(parseISO(date))
        })
      )
      setHeader(header)
      setValues(values)
      setLastValues(lastValues)
      setCompareValues(compareValues)
      if (!isEmpty(values)) {
        const chart = createDashboardAllChart(
          date,
          values,
          lastValues,
          compareValues,
          configChart,
          generateData,
          scrollRef
        )
        setChart(chart)
      }
    }
  }, [isLoading, rawData, date, configChart])
  const generateData = (): void => {
    const canvas = document.getElementById(
      'dashboard-all'
    ) as HTMLCanvasElement | null
    if (!isNil(canvas)) {
      canvas.toBlob((blob) => {
        setChartData(blob)
      })
    }
  }
  React.useEffect(() => {
    if (!isNil(chartData) && !isNil(tableData)) {
      const zip = JSZip()
      zip.file(`全社-${format('yyyy年MM月')(parseISO(date))}.png`, chartData)
      zip.file(`全社-${format('yyyy年MM月')(parseISO(date))}.xlsx`, tableData)
      zip
        .generateAsync({ type: 'blob' })
        .then((blob) => {
          setData(blob)
        })
        .catch(log.error)
    }
  }, [chartData, tableData])
  return (
    <>
      {isLoading ? (
        <Loading />
      ) : !isNil(chart) ? (
        <>
          <Box sx={{ border: 1 }}>
            <SolarChart chart={chart} id="dashboard-all" />
          </Box>
          <Box
            sx={{ border: 1, mt: '1em', backgroundColor: 'white', p: '1em' }}>
            <AllTable
              date={date}
              header={header}
              values={values}
              lastValues={lastValues}
              compareValues={compareValues}
              setTableData={setTableData}
            />
          </Box>
        </>
      ) : null}
    </>
  )
}
const Organization: React.FC<
  {
    partner: string
    date: string
    targetDate: string
    target?: string | null | undefined
    setData: (data: any) => void
    index: number
  } & ConfigProps &
    ScrollProps
> = ({
  partner,
  date,
  targetDate: targetDateArg,
  target: targetArg,
  config,
  setData,
  index,
  scrollRef
}) => {
  const notify = useNotify()
  const { data: mappings, isLoading: isLoadingMappings } =
    useGetMappings(partner)
  const { Chart: configChart } = config
  const { data: rawData, isLoading } = useGetElectricityYearly(
    date,
    ElectricityChartYearlyKind.lastYear
  )
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const [names, setNames] = React.useState<string[]>([])
  const [values, setValues] = React.useState<number[]>([])
  const [lastValues, setLastValues] = React.useState<number[]>([])
  const [compareValues, setCompareValues] = React.useState<
    Array<number | string>
  >([])
  const [tableData, setTableData] = React.useState<Uint8Array | undefined>(
    undefined
  )
  const [chartData, setChartData] = React.useState<any | undefined>(undefined)
  const [targetDate, setTargetDate] = React.useState<string | undefined>(
    undefined
  )
  const [target, setTarget] = React.useState<string | undefined>(undefined)
  const reduceData = (
    rawData: ElectricityYearly[],
    spins: string[],
    date: string
  ): number => {
    const targets = filter((item: ElectricityYearly) =>
      includes(item.spin)(spins)
    )(rawData)
    const data = groupBy<{ date: string; daily: number | null }>('date')(
      flatten(map((item: ElectricityYearly) => item.amounts)(targets))
    )
    return Math.floor(
      reduce(
        (sum: number, item: { daily: number | null }) =>
          sum + (item.daily ?? 0),
        0
      )(data[date])
    )
  }
  const { organizations } = config.Mappings
  React.useEffect(() => {
    if (!isNil(targetArg) && targetArg !== target) {
      setTarget(targetArg)
      setChart(undefined)
      setChartData(undefined)
    }
    if (targetDateArg !== targetDate) {
      setTargetDate(targetDateArg)
      setChart(undefined)
      setChartData(undefined)
    }
  }, [targetArg, targetDateArg])
  React.useEffect(() => {
    if (index > 0 && isNil(target)) return
    if (isLoading || isLoadingMappings) {
      notify('loading', { autoHideDuration: null })
    } else {
      notify('loading', { autoHideDuration: 0 })
    }
    if (!isNil(rawData) && !isNil(mappings) && !isNil(targetDate)) {
      const byOrganization = groupBy<Mapping>(
        organizations[index > 0 ? index - 1 : index]
      )(mappings)
      let names
      let items
      if (!isNil(target)) {
        items = byOrganization[target]
        names = map((item: Mapping) => item.name)(items)
      } else {
        names = keys(byOrganization)
        items = map((key: string) => byOrganization[key])(names)
      }
      const { values, lastValues, compareValues } = reduce(
        (
          sum: {
            values: number[]
            lastValues: number[]
            compareValues: Array<number | string>
          },
          item: Mapping | Mapping[]
        ) => {
          const spins = isArray(item)
            ? map((sub: Mapping) => sub.spin)(item)
            : split(',')(item.spin)
          const value = reduceData(compact(rawData.data), spins, targetDate)
          const lastValue = reduceData(
            compact(rawData.compareData),
            spins,
            format('yyyy-MM-dd')(subYears(1)(parseISO(targetDate)))
          )
          const compareValue = Math.floor(((value ?? 0) / lastValue) * 100)
          sum.values.push(value)
          sum.lastValues.push(lastValue)
          sum.compareValues.push(
            isNaN(compareValue) || compareValue === Number.POSITIVE_INFINITY
              ? '-'
              : compareValue
          )
          return sum
        },
        { values: [], lastValues: [], compareValues: [] }
      )(items)
      setNames(names)
      setValues(values)
      setLastValues(lastValues)
      setCompareValues(compareValues)
      if (!isEmpty(values)) {
        const chart = createDashboardOrganizationChart(
          targetDate,
          names,
          values,
          lastValues,
          compareValues,
          configChart,
          generateData,
          scrollRef
        )
        setChart(chart)
      }
    }
  }, [
    isLoading,
    isLoadingMappings,
    rawData,
    mappings,
    targetDate,
    target,
    date,
    index,
    organizations
  ])
  const generateData = (): void => {
    const canvas = document.getElementById(
      'dashboard-organization'
    ) as HTMLCanvasElement | null
    if (!isNil(canvas) && isNil(chartData)) {
      canvas.toBlob((blob) => {
        setChartData(blob)
      })
    }
  }
  React.useEffect(() => {
    if (!isNil(chartData) && !isNil(tableData)) {
      const zip = JSZip()
      zip.file(
        `${organizations[index] as string}-${
          !isNil(target) ? `${target}-` : ''
        }${format('yyyy年MM月')(parseISO(date))}.png`,
        chartData
      )
      zip.file(
        `${organizations[index] as string}-${
          !isNil(target) ? `${target}-` : ''
        }${format('yyyy年MM月')(parseISO(date))}.xlsx`,
        tableData
      )
      zip
        .generateAsync({ type: 'blob' })
        .then((blob) => {
          setData(blob)
        })
        .catch(log.error)
    }
  }, [chartData, tableData, organizations, index, target])
  return (
    <>
      {isLoading ? (
        <Loading />
      ) : !isNil(chart) && !isNil(targetDate) ? (
        <>
          <Box sx={{ border: 1 }}>
            <SolarChart chart={chart} id="dashboard-organization" />
          </Box>
          <Box
            sx={{ border: 1, mt: '1em', backgroundColor: 'white', p: '1em' }}>
            <OrganizationTable
              date={targetDate}
              header={names}
              values={values}
              lastValues={lastValues}
              compareValues={compareValues}
              setTableData={setTableData}
            />
          </Box>
        </>
      ) : null}
    </>
  )
}
// const First: React.FC<
//   {
//     partner: string
//     date: string
//     targetDate: string
//     setData: (data: any) => void
//   } & ConfigProps
// > = ({ partner, date, targetDate, config, setData }) => {
//   const notify = useNotify()
//   const { data: mappings, isLoading: isLoadingMappings } =
//     useGetMappings(partner)
//   const { Chart: configChart } = config
//   const { data: rawData, isLoading } = useGetElectricityYearly(
//     date,
//     ElectricityChartYearlyKind.lastYear
//   )
//   const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
//     undefined
//   )
//   const [names, setNames] = React.useState<string[]>([])
//   const [values, setValues] = React.useState<number[]>([])
//   const [lastValues, setLastValues] = React.useState<number[]>([])
//   const [compareValues, setCompareValues] = React.useState<
//     Array<number | string>
//   >([])
//   const [tableData, setTableData] = React.useState<Uint8Array | undefined>(
//     undefined
//   )
//   const [chartData, setChartData] = React.useState<any | undefined>(undefined)
//   const reduceData = (
//     rawData: ElectricityYearly[],
//     spins: string[],
//     date: string
//   ): number => {
//     const targets = filter((item: ElectricityYearly) =>
//       includes(item.spin)(spins)
//     )(rawData)
//     const data = groupBy<{ date: string; daily: number | null }>('date')(
//       flatten(map((item: ElectricityYearly) => item.amounts)(targets))
//     )
//     return Math.floor(
//       reduce(
//         (sum: number, item: { daily: number | null }) =>
//           sum + (item.daily ?? 0),
//         0
//       )(data[date])
//     )
//   }
//   const { organizations } = config.Mappings
//   React.useEffect(() => {
//     if (isLoading || isLoadingMappings) {
//       notify('loading', { autoHideDuration: null })
//     } else {
//       notify('loading', { autoHideDuration: 0 })
//     }
//     if (!isNil(rawData) && !isNil(mappings)) {
//       const byFirst = groupBy<Mapping>(organizations[0])(mappings)
//       const names = keys(byFirst)
//       const { values, lastValues, compareValues } = reduce(
//         (
//           sum: {
//             values: number[]
//             lastValues: number[]
//             compareValues: Array<number | string>
//           },
//           key: string
//         ) => {
//           const spins = map((item: Mapping) => item.spin)(byFirst[key])
//           const value = reduceData(compact(rawData.data), spins, targetDate)
//           const lastValue = reduceData(
//             compact(rawData.compareData),
//             spins,
//             format('yyyy-MM-dd')(subYears(1)(parseISO(targetDate)))
//           )
//           const compareValue = Math.floor(((value ?? 0) / lastValue) * 100)
//           sum.values.push(value)
//           sum.lastValues.push(lastValue)
//           sum.compareValues.push(
//             isNaN(compareValue) || compareValue === Number.POSITIVE_INFINITY
//               ? '-'
//               : compareValue
//           )
//           return sum
//         },
//         { values: [], lastValues: [], compareValues: [] }
//       )(names)
//       setNames(names)
//       setValues(values)
//       setLastValues(lastValues)
//       setCompareValues(compareValues)
//       if (!isEmpty(values)) {
//         const chart = createDashboardFirstChart(
//           targetDate,
//           names,
//           values,
//           lastValues,
//           compareValues,
//           configChart,
//           generateData
//         )
//         setChart(chart)
//       }
//     }
//   }, [isLoading, isLoadingMappings, rawData, mappings, targetDate, date])
//   const generateData = (): void => {
//     const canvas = document.getElementById('dashboard-first')
//     if (!isNil(canvas)) {
//       canvas.toBlob((blob) => {
//         setChartData(blob)
//       })
//     }
//   }
//   React.useEffect(() => {
//     if (!isNil(chartData) && !isNil(tableData)) {
//       const zip = JSZip()
//       zip.file(
//         `${organizations[0] as string}-${format('yyyy年MM月')(
//           parseISO(date)
//         )}.png`,
//         chartData
//       )
//       zip.file(
//         `${organizations[0] as string}-${format('yyyy年MM月')(
//           parseISO(date)
//         )}.xlsx`,
//         tableData
//       )
//       zip
//         .generateAsync({ type: 'blob' })
//         .then((blob) => {
//           setData(blob)
//         })
//         .catch(log.error)
//     }
//   }, [chartData, tableData])
//   return (
//     <>
//       {isLoading ? (
//         <Loading />
//       ) : !isNil(chart) ? (
//         <>
//           <Box sx={{ border: 1 }}>
//             <SolarChart chart={chart} id="dashboard-first" />
//           </Box>
//           <Box
//             sx={{ border: 1, mt: '1em', backgroundColor: 'white', p: '1em' }}>
//             <FirstTable
//               date={targetDate}
//               header={names}
//               values={values}
//               lastValues={lastValues}
//               compareValues={compareValues}
//               setTableData={setTableData}
//             />
//           </Box>
//         </>
//       ) : null}
//     </>
//   )
// }
// const Second: React.FC<
//   {
//     partner: string
//     date: string
//     targetDate: string
//     target: string
//     setData: (data: any) => void
//   } & ConfigProps
// > = ({ partner, date, targetDate, target, config, setData }) => {
//   const notify = useNotify()
//   const { data: mappings, isLoading: isLoadingMappings } =
//     useGetMappings(partner)
//   const { Chart: configChart } = config
//   const { data: rawData, isLoading } = useGetElectricityYearly(
//     date,
//     ElectricityChartYearlyKind.lastYear
//   )
//   const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
//     undefined
//   )
//   const [names, setNames] = React.useState<string[]>([])
//   const [values, setValues] = React.useState<number[]>([])
//   const [lastValues, setLastValues] = React.useState<number[]>([])
//   const [compareValues, setCompareValues] = React.useState<
//     Array<number | string>
//   >([])
//   const [tableData, setTableData] = React.useState<Uint8Array | undefined>(
//     undefined
//   )
//   const [chartData, setChartData] = React.useState<any | undefined>(undefined)
//   const reduceData = (
//     rawData: ElectricityYearly[],
//     spins: string[],
//     date: string
//   ): number => {
//     const targets = filter((item: ElectricityYearly) =>
//       includes(item.spin)(spins)
//     )(rawData)
//     const data = groupBy<{ date: string; daily: number | null }>('date')(
//       flatten(map((item: ElectricityYearly) => item.amounts)(targets))
//     )
//     return Math.floor(
//       reduce(
//         (sum: number, item: { daily: number | null }) =>
//           sum + (item.daily ?? 0),
//         0
//       )(data[date])
//     )
//   }
//   const { organizations } = config.Mappings
//   React.useEffect(() => {
//     if (isLoading || isLoadingMappings) {
//       notify('loading', { autoHideDuration: null })
//     } else {
//       notify('loading', { autoHideDuration: 0 })
//     }
//     if (!isNil(rawData) && !isNil(mappings)) {
//       const byFirst = groupBy<Mapping>(organizations[0])(mappings)
//       const items = byFirst[target]
//       const names = map((item: Mapping) => item.name)(items)
//       const { values, lastValues, compareValues } = reduce(
//         (
//           sum: {
//             values: number[]
//             lastValues: number[]
//             compareValues: Array<number | string>
//           },
//           item: Mapping
//         ) => {
//           const spins = split(',')(item.spin)
//           const value = reduceData(compact(rawData.data), spins, targetDate)
//           const lastValue = reduceData(
//             compact(rawData.compareData),
//             spins,
//             format('yyyy-MM-dd')(subYears(1)(parseISO(targetDate)))
//           )
//           const compareValue = Math.floor(((value ?? 0) / lastValue) * 100)
//           sum.values.push(value)
//           sum.lastValues.push(lastValue)
//           sum.compareValues.push(
//             isNaN(compareValue) || compareValue === Number.POSITIVE_INFINITY
//               ? '-'
//               : compareValue
//           )
//           return sum
//         },
//         { values: [], lastValues: [], compareValues: [] }
//       )(items)
//       setNames(names)
//       setValues(values)
//       setLastValues(lastValues)
//       setCompareValues(compareValues)
//       if (!isEmpty(values)) {
//         const chart = createDashboardSecondChart(
//           targetDate,
//           names,
//           values,
//           lastValues,
//           compareValues,
//           configChart,
//           generateData
//         )
//         setChart(chart)
//       }
//     }
//   }, [
//     isLoading,
//     isLoadingMappings,
//     rawData,
//     mappings,
//     targetDate,
//     target,
//     date
//   ])
//   const generateData = (): void => {
//     const canvas = document.getElementById('dashboard-second')
//     if (!isNil(canvas)) {
//       canvas.toBlob((blob) => {
//         setChartData(blob)
//       })
//     }
//   }
//   React.useEffect(() => {
//     if (!isNil(chartData) && !isNil(tableData)) {
//       const zip = JSZip()
//       zip.file(
//         `${organizations[1] as string}-${target as string}-${format(
//           'yyyy年MM月'
//         )(parseISO(date))}.png`,
//         chartData
//       )
//       zip.file(
//         `${organizations[1] as string}-${target as string}-${format(
//           'yyyy年MM月'
//         )(parseISO(date))}.xlsx`,
//         tableData
//       )
//       zip
//         .generateAsync({ type: 'blob' })
//         .then((blob) => {
//           setData(blob)
//         })
//         .catch(log.error)
//     }
//   }, [chartData, tableData])
//   return (
//     <>
//       {isLoading ? (
//         <Loading />
//       ) : !isNil(chart) ? (
//         <>
//           <Box sx={{ border: 1 }}>
//             <SolarChart chart={chart} id="dashboard-second" />
//           </Box>
//           <Box
//             sx={{ border: 1, mt: '1em', backgroundColor: 'white', p: '1em' }}>
//             <SecondTable
//               date={targetDate}
//               header={names}
//               values={values}
//               lastValues={lastValues}
//               compareValues={compareValues}
//               setTableData={setTableData}
//             />
//           </Box>
//         </>
//       ) : null}
//     </>
//   )
// }
const Place: React.FC<
  {
    partner: string
    date: string
    targetDate: string
    setData: (data: any) => void
    permissions: string[]
  } & ConfigProps &
    ScrollProps
> = ({
  partner,
  date,
  targetDate,
  config,
  setData,
  permissions,
  scrollRef
}) => {
  const notify = useNotify()
  const { data: mappings, isLoading: isLoadingMappings } =
    useGetMappings(partner)
  const { data: identity, isLoading: isLoadingIdentity } = useGetIdentity()
  const { Chart: configChart } = config
  const { data: rawData, isLoading } = useGetElectricityYearly(
    date,
    ElectricityChartYearlyKind.lastYear
  )
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const [names, setNames] = React.useState<string[]>([])
  const [ids, setIds] = React.useState<string[]>([])
  const [values, setValues] = React.useState<number[]>([])
  const [lastValues, setLastValues] = React.useState<number[]>([])
  const [compareValues, setCompareValues] = React.useState<
    Array<number | string>
  >([])
  const [tableData, setTableData] = React.useState<Uint8Array | undefined>(
    undefined
  )
  const [chartData, setChartData] = React.useState<any | undefined>(undefined)
  const reduceData = (
    rawData: ElectricityYearly[],
    spins: string[],
    date: string
  ): number => {
    const targets = filter((item: ElectricityYearly) =>
      includes(item.spin)(spins)
    )(rawData)
    const data = groupBy<{ date: string; daily: number | null }>('date')(
      flatten(map((item: ElectricityYearly) => item.amounts)(targets))
    )
    return Math.floor(
      reduce(
        (sum: number, item: { daily: number | null }) =>
          sum + (item.daily ?? 0),
        0
      )(data[date])
    )
  }
  const { name: nameKey } = config.Mappings
  React.useEffect(() => {
    if (isLoading || isLoadingMappings || isLoadingIdentity) {
      notify('loading', { autoHideDuration: null })
    } else {
      notify('loading', { autoHideDuration: 0 })
    }
    if (!isNil(rawData) && !isNil(mappings) && !isNil(identity)) {
      const {
        fullName,
        cognitoUser: {
          signInUserSession: {
            idToken: {
              payload: { email }
            }
          }
        }
      } = identity
      let items: Mapping[] | undefined
      if (includes('manager')(permissions) || includes('user')(permissions)) {
        const byManaged = groupBy<Mapping>('managedBy')(mappings)
        items = byManaged[fullName as string]
      }
      if (includes('owner')(permissions)) {
        const byOwned = groupBy<Mapping>('ownedBy')(mappings)
        items = byOwned[email as string]
      }
      if (isEmpty(items) && !isNil(process.env['TEST'])) {
        items = slice(0, 4)(mappings)
      }
      const names = map((item: Mapping) => item.name)(items)
      const ids = map((item: Mapping) => item.id)(items)
      const { values, lastValues, compareValues } = reduce(
        (
          sum: {
            values: number[]
            lastValues: number[]
            compareValues: Array<number | string>
          },
          item: Mapping
        ) => {
          const spins = split(',')(item.spin)
          const value = reduceData(compact(rawData.data), spins, targetDate)
          const lastValue = reduceData(
            compact(rawData.compareData),
            spins,
            format('yyyy-MM-dd')(subYears(1)(parseISO(targetDate)))
          )
          const compareValue = Math.floor(((value ?? 0) / lastValue) * 100)
          sum.values.push(value)
          sum.lastValues.push(lastValue)
          sum.compareValues.push(
            isNaN(compareValue) || compareValue === Number.POSITIVE_INFINITY
              ? '-'
              : compareValue
          )
          return sum
        },
        { values: [], lastValues: [], compareValues: [] }
      )(items)
      setNames(names)
      setIds(ids)
      setValues(values)
      setLastValues(lastValues)
      setCompareValues(compareValues)
      if (!isEmpty(values)) {
        const chart = createDashboardOrganizationChart(
          targetDate,
          names,
          values,
          lastValues,
          compareValues,
          configChart,
          generateData,
          scrollRef
        )
        setChart(chart)
      }
    }
  }, [
    isLoading,
    isLoadingMappings,
    isLoadingIdentity,
    rawData,
    mappings,
    targetDate,
    date
  ])
  const generateData = (): void => {
    const canvas = document.getElementById(
      'dashboard-third'
    ) as HTMLCanvasElement | null
    if (!isNil(canvas)) {
      canvas.toBlob((blob) => {
        setChartData(blob)
      })
    }
  }
  React.useEffect(() => {
    if (isLoadingIdentity || isNil(identity)) return
    const { fullName } = identity
    if (!isNil(chartData) && !isNil(tableData)) {
      const zip = JSZip()
      zip.file(
        `担当${nameKey as string}-${fullName as string}-${format('yyyy年MM月')(
          parseISO(date)
        )}.png`,
        chartData
      )
      zip.file(
        `担当${nameKey as string}-${fullName as string}-${format('yyyy年MM月')(
          parseISO(date)
        )}.xlsx`,
        tableData
      )
      zip
        .generateAsync({ type: 'blob' })
        .then((blob) => {
          setData(blob)
        })
        .catch(log.error)
    }
  }, [chartData, tableData])
  return (
    <>
      {isLoading ? (
        <Loading />
      ) : !isNil(chart) ? (
        <>
          <Box sx={{ border: 1 }}>
            <SolarChart chart={chart} id="dashboard-third" />
          </Box>
          <Box
            sx={{ border: 1, mt: '1em', backgroundColor: 'white', p: '1em' }}>
            <PlaceTable
              date={targetDate}
              header={names}
              values={values}
              lastValues={lastValues}
              compareValues={compareValues}
              ids={ids}
              setTableData={setTableData}
            />
          </Box>
        </>
      ) : null}
    </>
  )
}
const Ranking: React.FC<
  {
    partner: string
    date: string
    targetDate: string
    setData: (data: any) => void
    permissions: string[]
  } & ConfigProps &
    ScrollProps
> = ({ partner, date, targetDate, setData, config, permissions }) => {
  const notify = useNotify()
  const [names, setNames] = React.useState<
    Array<{
      name: string
      area: string
      id: string
      floorArea: number | null | undefined
    }>
  >([])
  const [values, setValues] = React.useState<number[]>([])
  const [lastValues, setLastValues] = React.useState<number[]>([])
  const [compareValues, setCompareValues] = React.useState<
    Array<string | number>
  >([])
  const [tableData, setTableData] = React.useState<Uint8Array | undefined>(
    undefined
  )
  const [scroll, setScroll] = React.useState<
    React.RefObject<HTMLDivElement> | undefined
  >(undefined)
  // const { data, isLoading, isFetching } = useGetReducedElectricityYearly(
  //   partner,
  //   date,
  //   targetDate
  // )
  // React.useEffect(() => {
  //   if (isLoading || isFetching) {
  //     notify('loading', { autoHideDuration: null })
  //   } else {
  //     notify('loading', { autoHideDuration: 0 })
  //   }
  //   if (!isNil(data)) {
  //     const { names, values, lastValues, compareValues } = data
  //     setNames(names)
  //     setValues(values)
  //     setLastValues(lastValues)
  //     setCompareValues(compareValues)
  //   }
  // }, [isLoading, data])
  // log.info({ isLoading })
  const { data: mappingsRaw, isLoading: isLoadingMappings } =
    useGetMappings(partner)
  const { data: rawData, isLoading } = useGetElectricityYearly(
    date,
    ElectricityChartYearlyKind.lastYear
  )
  const { identity, isLoading: isLoadingIdentity } = useGetIdentity()
  const reduceData = (
    rawData: ElectricityYearly[],
    spins: string[],
    date: string
  ): number => {
    const bySpins = groupBy<ElectricityYearly>('spin')(rawData)
    const targets = compact(map((spin: string) => head(bySpins[spin]))(spins))
    // const targets = filter((item: ElectricityYearly) =>
    //   includes(item.spin)(spins)
    // )(rawData)
    const data = groupBy<{ date: string; daily: number | null }>('date')(
      flatten(map((item: ElectricityYearly) => item.amounts)(targets))
    )
    return Math.floor(
      reduce(
        (sum: number, item: { daily: number | null }) =>
          sum + (item.daily ?? 0),
        0
      )(data[date])
    )
  }
  const [remains, setRemains] = React.useState<number>(0)
  const [mappings, setMappings] = React.useState<Mapping[]>([])
  const [area, setArea] = React.useState<string | undefined>(undefined)
  const { organizations: organizationsKey } = config.Mappings
  React.useEffect(() => {
    if (isLoading || isLoadingMappings || isLoadingIdentity) {
      notify('loading', { autoHideDuration: null })
    } else {
      notify('loading', { autoHideDuration: 0 })
    }
    if (isNil(mappingsRaw)) return
    let data: Mapping[] = mappingsRaw
    if (!isNil(identity)) {
      const {
        cognitoUser: {
          signInUserSession: {
            idToken: {
              payload: { email, 'custom:organizations': organizations }
            }
          }
        }
      } = identity
      if (includes('owner')(permissions)) {
        data = filter((item: Mapping) => item.ownedBy === email)(mappingsRaw)
      } else if (
        !isNil(
          find(
            (item: string) =>
              startsWith('manager')(item) && item !== 'manager#0'
          )(permissions)
        ) ||
        includes('user')(permissions)
      ) {
        const areaKey = head(split(',')(organizationsKey))
        const area = head(split(',')(organizations))
        if (isNil(area) || isNil(areaKey)) {
          data = []
        } else {
          data = filter((item: Mapping) =>
            includes(area)((item[areaKey] as string | undefined) ?? [])
          )(mappingsRaw)
          setArea(area)
        }
      }
    }
    setMappings(data)
    setRemains(size(data))
    setNames([])
    setValues([])
    setLastValues([])
    setCompareValues([])
  }, [
    isLoading,
    isLoadingMappings,
    isLoadingIdentity,
    identity,
    targetDate,
    organizationsKey,
    mappingsRaw
  ])
  const bulkSize = 100
  React.useEffect(() => {
    if (remains === 0) return
    const start = size(mappings) - remains
    const items = slice(start, start + bulkSize)(mappings)
    if (!isNil(rawData) && !isNil(mappings)) {
      const sum = reduce(
        (
          sum: {
            names: Array<{
              name: string
              area: string
              id: string
              floorArea: number | null | undefined
            }>
            values: number[]
            lastValues: number[]
            compareValues: Array<number | string>
          },
          item: Mapping
        ) => {
          const { name, id, floorArea } = item
          const area = (item[organizationsKey[0]] ?? '') as string
          const spins = split(',')(item.spin)
          const value = reduceData(compact(rawData.data), spins, targetDate)
          const lastValue = reduceData(
            compact(rawData.compareData),
            spins,
            format('yyyy-MM-dd')(subYears(1)(parseISO(targetDate)))
          )
          const compareValue = Math.floor(((value ?? 0) / lastValue) * 100)
          sum.names.push({ name, area, id, floorArea })
          sum.values.push(value)
          sum.lastValues.push(lastValue)
          sum.compareValues.push(
            isNaN(compareValue) || compareValue === Number.POSITIVE_INFINITY
              ? '-'
              : compareValue
          )
          return sum
        },
        { names: [], values: [], lastValues: [], compareValues: [] }
      )(items)
      setNames(concat(names)(sum.names))
      setValues(concat(values)(sum.values))
      setLastValues(concat(lastValues)(sum.lastValues))
      setCompareValues(concat(compareValues)(sum.compareValues))
      if (remains - bulkSize > 0) {
        setRemains(remains - bulkSize)
      } else {
        setRemains(0)
      }
    }
  }, [
    rawData,
    mappings,
    targetDate,
    remains,
    names,
    values,
    lastValues,
    compareValues
  ])
  React.useEffect(() => {
    if (!isNil(tableData)) {
      const zip = JSZip()
      zip.file(
        `ランキング-${format('yyyy年MM月')(parseISO(date))}.xlsx`,
        tableData
      )
      zip
        .generateAsync({ type: 'blob' })
        .then((blob) => {
          setData(blob)
        })
        .catch(log.error)
    }
  }, [tableData])
  if (isEmpty(values)) return null
  return (
    <>
      {isLoading || isLoadingMappings || remains > 0 ? (
        <Loading />
      ) : (
        <ScrollTop
          setScroll={setScroll}
          renderItem={() => (
            <RankingTable
              names={names}
              values={values}
              lastValues={lastValues}
              compareValues={compareValues}
              config={config}
              setTableData={setTableData}
              area={area}
              scrollRef={scroll}
            />
          )}
        />
      )}
    </>
  )
}
const DemandAll: React.FC<
  { partner: string; date: string; targetDate: string } & ConfigProps &
    ScrollProps
> = ({ date, targetDate, config, scrollRef }) => {
  const { Chart: configChart } = config
  const notify = useNotify()
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const { data: demandDataMonthly, isLoading } =
    useGetElectricityDemandMonthly(targetDate)
  React.useEffect(() => {
    if (isLoading) {
      notify('loading', { autoHideDuration: null })
    } else {
      notify('loading', { autoHideDuration: 0 })
    }
    if (!isNil(demandDataMonthly)) {
      const { demands } = demandDataMonthly
      const values = reduce((sum: number[], item: { demands: number[] }) => {
        const { demands } = item
        map((index: number) => {
          sum[index] = (sum[index] ?? 0) + 1
        })(demands)
        return sum
      }, new Array(48))(demands)
      const chart = createDemandMonthlyChart(values, configChart, scrollRef)
      setChart(chart)
    }
  }, [isLoading, demandDataMonthly, targetDate, date])
  return (
    <>
      {isLoading ? (
        <Loading />
      ) : !isNil(chart) ? (
        <SolarChart chart={chart} />
      ) : null}
    </>
  )
}
// const DemandFirst: React.FC<
//   {
//     partner: string
//     date: string
//     targetDate: string
//     target: string | null | undefined
//   } & ConfigProps
// > = ({ partner, date, targetDate, config, target }) => {
//   const { Chart: configChart } = config
//   const notify = useNotify()
//   const { data: mappings, isLoading: isLoadingMappings } =
//     useGetMappings(partner)
//   const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
//     undefined
//   )
//   const { data: demandDataMonthly, isLoading } =
//     useGetElectricityDemandMonthly(targetDate)
//   const { organizations } = config.Mappings
//   React.useEffect(() => {
//     if (isLoading || isLoadingMappings) {
//       notify('loading', { autoHideDuration: null })
//     } else {
//       notify('loading', { autoHideDuration: 0 })
//     }
//     if (!isNil(demandDataMonthly) && !isNil(mappings) && !isNil(target)) {
//       const byFirst = groupBy<Mapping>(organizations[0])(mappings)
//       const targets = map((item: Mapping) => item.spin)(byFirst[target])
//       const bySpins = groupBy<any>('spin')(demandDataMonthly.demands)
//       const demands = flatten(
//         map((spins: string) =>
//           flatten(
//             compact(map((spin: string) => bySpins[spin])(split(',')(spins)))
//           )
//         )(targets)
//       )
//       const values = reduce((sum: number[], item: { demands: number[] }) => {
//         const { demands } = item
//         map((index: number) => {
//           sum[index] = (sum[index] ?? 0) + 1
//         })(demands)
//         return sum
//       }, new Array(48))(demands)
//       const chart = createDemandMonthlyChart(values, configChart)
//       setChart(chart)
//     }
//   }, [
//     isLoading,
//     isLoadingMappings,
//     demandDataMonthly,
//     mappings,
//     targetDate,
//     date,
//     target
//   ])
//   return (
//     <>
//       {isLoading ? (
//         <Loading />
//       ) : !isNil(chart) ? (
//         <SolarChart chart={chart} />
//       ) : null}
//     </>
//   )
// }
// const DemandSecond: React.FC<
//   {
//     partner: string
//     date: string
//     targetDate: string
//     target: string | null | undefined
//   } & ConfigProps
// > = ({ partner, date, targetDate, config, target }) => {
//   const { Chart: configChart } = config
//   const notify = useNotify()
//   const { data: mappings, isLoading: isLoadingMappings } =
//     useGetMappings(partner)
//   const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
//     undefined
//   )
//   const { data: demandDataMonthly, isLoading } =
//     useGetElectricityDemandMonthly(targetDate)
//   React.useEffect(() => {
//     if (isNil(target)) return
//     if (isLoading || isLoadingMappings) {
//       notify('loading', { autoHideDuration: null })
//     } else {
//       notify('loading', { autoHideDuration: 0 })
//     }
//     if (!isNil(demandDataMonthly) && !isNil(mappings)) {
//       const byId = groupBy<Mapping>('id')(mappings)
//       const targets = map((item: Mapping) => item.spin)(byId[target])
//       const bySpins = groupBy<any>('spin')(demandDataMonthly.demands)
//       const demands = flatten(
//         map((spins: string) =>
//           flatten(
//             compact(map((spin: string) => bySpins[spin])(split(',')(spins)))
//           )
//         )(targets)
//       )
//       const values = reduce((sum: number[], item: { demands: number[] }) => {
//         const { demands } = item
//         map((index: number) => {
//           sum[index] = (sum[index] ?? 0) + 1
//         })(demands)
//         return sum
//       }, new Array(48))(demands)
//       const chart = createDemandMonthlyChart(values, configChart)
//       setChart(chart)
//     }
//   }, [
//     isLoading,
//     isLoadingMappings,
//     demandDataMonthly,
//     mappings,
//     targetDate,
//     date,
//     target
//   ])
//   return (
//     <>
//       {isLoading ? (
//         <Loading />
//       ) : !isNil(chart) ? (
//         <SolarChart chart={chart} />
//       ) : null}
//     </>
//   )
// }
const DemandOrganization: React.FC<
  {
    partner: string
    date: string
    targetDate: string
    target: string | null | undefined
    index: number
  } & ConfigProps &
    ScrollProps
> = ({ partner, date, targetDate, config, target, index, scrollRef }) => {
  const { Chart: configChart, Mappings: configMappings } = config
  const { organizations } = configMappings
  const notify = useNotify()
  const { data: mappings, isLoading: isLoadingMappings } =
    useGetMappings(partner)
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const { data: demandDataMonthly, isLoading } =
    useGetElectricityDemandMonthly(targetDate)
  React.useEffect(() => {
    if (isNil(target)) return
    if (isLoading || isLoadingMappings) {
      notify('loading', { autoHideDuration: null })
    } else {
      notify('loading', { autoHideDuration: 0 })
    }
    if (!isNil(demandDataMonthly) && !isNil(mappings)) {
      const byOrganization = groupBy<Mapping>(organizations[index])(mappings)
      const targets = map((item: Mapping) => item.spin)(byOrganization[target])
      const bySpins = groupBy<any>('spin')(demandDataMonthly.demands)
      const demands = flatten(
        map((spins: string) =>
          flatten(
            compact(map((spin: string) => bySpins[spin])(split(',')(spins)))
          )
        )(targets)
      )
      const values = reduce((sum: number[], item: { demands: number[] }) => {
        const { demands } = item
        map((index: number) => {
          sum[index] = (sum[index] ?? 0) + 1
        })(demands)
        return sum
      }, new Array(48))(demands)
      const chart = createDemandMonthlyChart(values, configChart, scrollRef)
      setChart(chart)
    }
  }, [
    isLoading,
    isLoadingMappings,
    demandDataMonthly,
    mappings,
    targetDate,
    date,
    target,
    organizations,
    index
  ])
  return (
    <>
      {isLoading ? (
        <Loading />
      ) : !isNil(chart) ? (
        <SolarChart chart={chart} />
      ) : null}
    </>
  )
}
const DemandPlace: React.FC<
  {
    partner: string
    date: string
    targetDate: string
  } & ConfigProps &
    ScrollProps
> = ({ partner, date, targetDate, config, scrollRef }) => {
  const { Chart: configChart } = config
  const notify = useNotify()
  const { data: identity, isLoading: isLoadingIdentity } = useGetIdentity()
  const { data: mappings, isLoading: isLoadingMappings } =
    useGetMappings(partner)
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const { data: demandDataMonthly, isLoading } =
    useGetElectricityDemandMonthly(targetDate)
  React.useEffect(() => {
    if (isLoading || isLoadingMappings || isLoadingIdentity) {
      notify('loading', { autoHideDuration: null })
    } else {
      notify('loading', { autoHideDuration: 0 })
    }
    if (!isNil(demandDataMonthly) && !isNil(mappings) && !isNil(identity)) {
      const { fullName } = identity
      const byManaged = groupBy<Mapping>('managedBy')(mappings)
      let targets = map((item: Mapping) => item.spin)(
        byManaged[fullName as string]
      )
      if (isEmpty(targets) && !isNil(process.env['TEST'])) {
        targets = map((item: Mapping) => item.spin)(slice(0, 4)(mappings))
      }
      const bySpins = groupBy<any>('spin')(demandDataMonthly.demands)
      const demands = flatten(
        map((spins: string) =>
          flatten(
            compact(map((spin: string) => bySpins[spin])(split(',')(spins)))
          )
        )(targets)
      )
      const values = reduce((sum: number[], item: { demands: number[] }) => {
        const { demands } = item
        map((index: number) => {
          sum[index] = (sum[index] ?? 0) + 1
        })(demands)
        return sum
      }, new Array(48))(demands)
      const chart = createDemandMonthlyChart(values, configChart, scrollRef)
      setChart(chart)
    }
  }, [
    isLoading,
    isLoadingMappings,
    demandDataMonthly,
    mappings,
    targetDate,
    date
  ])
  return (
    <>
      {isLoading ? (
        <Loading />
      ) : !isNil(chart) ? (
        <SolarChart chart={chart} />
      ) : null}
    </>
  )
}
const Dashboard2Content: React.FC<
  {
    partner: string
    permissions: string[]
  } & ConfigProps &
    ScrollProps
> = ({ partner, permissions, config, scrollRef }) => {
  const notify = useNotify()
  const { maxDate, startMonthOfFiscalYear, Mappings: configMappings } = config
  const { data: identity, isLoading: isLoadingIdentity } = useGetIdentity()
  const { organizations } = configMappings
  const isManager =
    isNil(
      find((item: string) => item === 'highManager' || item === 'manager#0')(
        permissions
      )
    ) &&
    !isNil(find((item: string) => startsWith('manager')(item))(permissions))
  const [tab, setTab] = React.useState<number>(0)
  const [demandTab, setDemandTab] = React.useState<number>(0)
  const [targetDate, setTargetDate] = React.useState<string>(
    !isNil(maxDate)
      ? maxDate
      : format('yyyy-MM-dd')(
          startOfMonth(
            isAfter(parseISO(`${format('yyyy-MM-dd')(Date.now())}T15:00`))(
              Date.now()
            )
              ? subDays(1)(Date.now())
              : subDays(2)(Date.now())
          )
        )
  )
  const [target, setTarget] = React.useState<string | undefined>(undefined)
  const [data, setData] = React.useState<any | undefined>(undefined)
  const [area, setArea] = React.useState<string | undefined>(undefined)
  const translate = useTranslate()
  const { setOpen } = React.useContext(DrawerContext)
  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: number
  ): void => {
    setOpen(false)
    setTab(newValue)
  }
  const handleDemandChange = (
    _event: React.SyntheticEvent,
    newValue: number
  ): void => {
    setDemandTab(newValue)
    setTarget(
      demandTabs[newValue] === `#${organizations[0] as string}`
        ? area
        : undefined
    )
  }
  React.useEffect(() => {
    if (isLoadingIdentity || !isManager) return
    if (!isNil(identity)) {
      const {
        cognitoUser: {
          signInUserSession: {
            idToken: {
              payload: { 'custom:organizations': organizations }
            }
          }
        }
      } = identity
      if (isNil(organizations)) return
      const area = head(split(',')(organizations))
      if (isNil(area)) return
      setArea(area)
      setTarget(area)
    }
  }, [isLoadingIdentity, identity, isManager])
  if (isLoadingIdentity) return null
  if (
    !isNil(
      find(
        (item: string) => startsWith('manager')(item) && item !== 'manager#0'
      )(permissions)
    ) &&
    isNil(area)
  ) {
    notify('no area')
    return null
  }
  const fontSize = window.innerWidth <= 900 ? 14 : 16
  const tabItems = compact(
    map((kind: string) => {
      if (
        (kind === 'rank' && !includes('partner')(permissions)) ||
        (includes(kind)(
          map((item: string) => `#${item}`)(
            slice(1, size(organizations))(organizations)
          )
        ) &&
          !includes('user')(permissions) &&
          !includes('owner')(permissions) &&
          !includes('partner')(permissions)) ||
        (kind !== 'place' &&
          (includes('highManager')(permissions) ||
            includes('manager#0')(permissions))) ||
        (kind === 'place' &&
          (includes('user')(permissions) ||
            includes('manager')(permissions))) ||
        (kind === 'demand' &&
          !isNil(
            find(
              (item: string) =>
                startsWith('manager')(item) && item !== 'manager#0'
            )(permissions)
          )) ||
        (kind === 'compare' &&
          !includes('user')(permissions) &&
          !includes('owner')(permissions) &&
          !includes('partner')(permissions))
      ) {
        return {
          kind,
          item: (
            <Tab
              sx={{
                width: window.innerWidth <= 900 ? '6em' : '7.5em',
                border: 0.6
              }}
              label={map((line: string) => (
                <Typography variant="inherit" sx={{ fontSize }} key={line}>
                  {line}
                </Typography>
              ))(
                split('\n')(
                  !startsWith('#')(kind)
                    ? translate(`resources.dashboard2.tabs.${kind}`)
                    : replace('#', '')(kind)
                )
              )}
              key={kind}
            />
          )
        }
      }
      return null
    })([
      'all',
      ...map((item: string) => `#${item}`)(organizations),
      'place',
      'rank',
      'demand',
      'compare'
    ])
  )
  const tabs = map((item: { kind: string }) => item.kind)(tabItems)
  const demand = findIndex((kind: string) => kind === 'demand')(tabs)
  const demandTabItems = compact(
    map(
      (kind: string) =>
        ((kind === 'all' &&
          !isNil(
            find(
              (item: string) => item === 'highManager' || item === 'manager#0'
            )(permissions)
          )) ||
          (kind !== 'all' && kind !== 'third') ||
          (kind === 'third' &&
            !isNil(
              find((item: string) => item === 'manager')(permissions)
            ))) && {
          kind,
          item: (
            <Tab
              sx={{
                width: window.innerWidth <= 900 ? '6em' : '7.5em',
                border: 0.6
              }}
              label={map((line: string) => (
                <Typography variant="inherit" sx={{ fontSize }} key={line}>
                  {line}
                </Typography>
              ))(
                split('\n')(
                  !startsWith('#')(kind)
                    ? translate(`resources.dashboard2.demandTabs.${kind}`)
                    : replace('#', '')(kind)
                )
              )}
              key={kind}
            />
          )
        }
    )(['all', ...map((item: string) => `#${item}`)(organizations), 'third'])
  )
  const demandTabs = map((item: { kind: string }) => item.kind)(demandTabItems)
  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Tabs
          value={tab}
          onChange={handleChange}
          TabIndicatorProps={{ style: { background: 'red' } }}>
          {map((item: { item: ReactElement }) => item.item)(tabItems)}
        </Tabs>
      </Grid>
      {(demand === -1 || tab < demand) && (
        <Grid size={12}>
          <DashboardControl
            date={includes('owner')(permissions) || tab !== 0}
            index={
              !startsWith('#')(tabs[tab] as string)
                ? undefined
                : tab === 0
                ? undefined
                : tab - 2
            }
            partner={partner}
            config={config}
            setTargetDate={setTargetDate}
            setTarget={setTarget}
            targetDate={targetDate}
            noDownlad
            data={data}
          />
        </Grid>
      )}
      <Grid size={12}>
        {tabs[tab] === 'all' && (
          <All
            date={
              isBefore(
                parseISO(
                  `${getYear(Date.now())}-${
                    startMonthOfFiscalYear as string
                  }-01`
                )
              )(parseISO(targetDate))
                ? `${getYear(Date.now()) - 1}-${
                    startMonthOfFiscalYear as string
                  }-01`
                : `${getYear(Date.now())}-${
                    startMonthOfFiscalYear as string
                  }-01`
            }
            config={config}
            setData={setData}
            scrollRef={scrollRef}
          />
        )}
        {map(
          (index: number) =>
            tabs[tab] === `#${organizations[index] as string}` && (
              <Organization
                partner={partner}
                date={
                  isBefore(
                    parseISO(
                      `${getYear(parseISO(targetDate))}-${
                        startMonthOfFiscalYear as string
                      }-01`
                    )
                  )(parseISO(targetDate))
                    ? `${getYear(parseISO(targetDate)) - 1}-${
                        startMonthOfFiscalYear as string
                      }-01`
                    : `${getYear(parseISO(targetDate))}-${
                        startMonthOfFiscalYear as string
                      }-01`
                }
                targetDate={targetDate}
                target={index > 0 ? target : undefined}
                config={config}
                setData={setData}
                index={index}
                scrollRef={scrollRef}
                key={index}
              />
            )
        )(range(0)(size(organizations)))}
        {/* {tab === 2 && size(organizations) > 1 && (
          <Organization
            partner={partner}
            date={
              isBefore(
                parseISO(
                  `${getYear(parseISO(targetDate))}-${
                    startMonthOfFiscalYear as string
                  }-01`
                )
              )(Date.now())
                ? `${getYear(parseISO(targetDate)) - 1}-${
                    startMonthOfFiscalYear as string
                  }-01`
                : `${getYear(parseISO(targetDate))}-${
                    startMonthOfFiscalYear as string
                  }-01`
            }
            targetDate={targetDate}
            target={target ?? ''}
            config={config}
            setData={setData}
            index={tab - 2}
          />
        )}
        {tab === 3 && size(organizations) > 2 && (
          <Organization
            partner={partner}
            date={
              isBefore(
                parseISO(
                  `${getYear(parseISO(targetDate))}-${
                    startMonthOfFiscalYear as string
                  }-01`
                )
              )(Date.now())
                ? `${getYear(parseISO(targetDate)) - 1}-${
                    startMonthOfFiscalYear as string
                  }-01`
                : `${getYear(parseISO(targetDate))}-${
                    startMonthOfFiscalYear as string
                  }-01`
            }
            targetDate={targetDate}
            target={target ?? ''}
            config={config}
            setData={setData}
            index={tab - 2}
          />
        )} */}
        {tabs[tab] === 'place' && (
          <Place
            partner={partner}
            date={
              isBefore(
                parseISO(
                  `${getYear(parseISO(targetDate))}-${
                    startMonthOfFiscalYear as string
                  }-01`
                )
              )(parseISO(targetDate))
                ? `${getYear(parseISO(targetDate)) - 1}-${
                    startMonthOfFiscalYear as string
                  }-01`
                : `${getYear(parseISO(targetDate))}-${
                    startMonthOfFiscalYear as string
                  }-01`
            }
            targetDate={targetDate}
            config={config}
            setData={setData}
            permissions={permissions}
            scrollRef={scrollRef}
          />
        )}
        {tabs[tab] === 'rank' && (
          <Ranking
            partner={partner}
            date={
              isBefore(
                parseISO(
                  `${getYear(parseISO(targetDate))}-${
                    startMonthOfFiscalYear as string
                  }-01`
                )
              )(parseISO(targetDate))
                ? `${getYear(parseISO(targetDate)) - 1}-${
                    startMonthOfFiscalYear as string
                  }-01`
                : `${getYear(parseISO(targetDate))}-${
                    startMonthOfFiscalYear as string
                  }-01`
            }
            targetDate={targetDate}
            config={config}
            setData={setData}
            permissions={permissions}
            scrollRef={scrollRef}
          />
        )}
        {tabs[tab] === 'demand' && (
          <>
            <Box
              sx={{
                ml:
                  window.innerWidth >= 900
                    ? `${7.5 * demand}em`
                    : `${5.4 * demand}em`,
                mt: window.innerWidth <= 900 ? '-0.8em' : '-1em',
                mb: '1em'
              }}>
              <Tabs
                value={demandTab}
                onChange={handleDemandChange}
                TabIndicatorProps={{ style: { background: 'red' } }}>
                {map((item: { item: ReactElement }) => item.item)(
                  demandTabItems
                )}
              </Tabs>
            </Box>
            <Grid container>
              <Grid size={12}>
                <DashboardControl
                  date={true}
                  index={
                    !startsWith('#')(demandTabs[demandTab] as string)
                      ? undefined
                      : demandTab === 0
                      ? undefined
                      : demandTabs[0] === 'all'
                      ? demandTab - 1
                      : demandTab
                  }
                  // spot={demandTabs[demandTab] === 'third'}
                  // first={demandTab === 1}
                  // second={demandTab === 2}
                  partner={partner}
                  config={config}
                  target={target}
                  setTargetDate={setTargetDate}
                  setTarget={setTarget}
                  targetDate={targetDate}
                  noDownload
                />
              </Grid>
            </Grid>
            <DemandTabs
              partner={partner}
              date={
                isBefore(
                  parseISO(
                    `${getYear(parseISO(targetDate))}-${
                      startMonthOfFiscalYear as string
                    }-01`
                  )
                )(parseISO(targetDate))
                  ? `${getYear(parseISO(targetDate)) - 1}-${
                      startMonthOfFiscalYear as string
                    }-01`
                  : `${getYear(parseISO(targetDate))}-${
                      startMonthOfFiscalYear as string
                    }-01`
              }
              targetDate={targetDate}
              config={config}
              demandTab={demandTab}
              target={target}
              permissions={permissions}
              tabs={demandTabs}
              scrollRef={scrollRef}
            />
          </>
        )}
        {tabs[tab] === 'compare' && <Compare config={config} />}
      </Grid>
    </Grid>
  )
}
export const DemandTabs: React.FC<
  {
    partner: string
    date: string
    targetDate: string
    target: string | null | undefined
    demandTab: number
    tabs: string[]
  } & ConfigProps &
    ScrollProps
> = ({
  partner,
  date,
  targetDate,
  config,
  demandTab,
  target,
  tabs,
  scrollRef
}) => {
  const { Mappings: configMappings } = config
  const { organizations } = configMappings
  return (
    <>
      <Box>
        {tabs[demandTab] === 'all' && (
          <>
            <Grid size={12} sx={{ mt: '1em', mb: '1em' }}>
              <div id="demand" />
            </Grid>
            <Box>
              <DemandAll
                partner={partner}
                date={date}
                targetDate={targetDate}
                config={config}
                scrollRef={scrollRef}
              />
            </Box>
          </>
        )}
        {map(
          (index: number) =>
            tabs[demandTab] === `#${organizations[index] as string}` && (
              <div key={index}>
                <Grid size={12} sx={{ mt: '1em', mb: '1em' }}>
                  <div id="demand" />
                </Grid>
                <Box>
                  <DemandOrganization
                    partner={partner}
                    date={date}
                    targetDate={targetDate}
                    config={config}
                    target={target}
                    index={index}
                    scrollRef={scrollRef}
                  />
                </Box>
              </div>
            )
        )(range(0)(size(organizations)))}
        {/* {demandTab === 1 && (
          <>
            <Grid size={12} sx={{ mt: '1em', mb: '1em' }}>
              <div id="demand" />
            </Grid>
            <Box>
              <DemandFirst
                partner={partner}
                date={date}
                targetDate={targetDate}
                config={config}
                target={target}
              />
            </Box>
          </>
        )}
        {demandTab === 2 && (
          <>
            <Grid size={12} sx={{ mt: '1em', mb: '1em' }}>
              <div id="demand" />
            </Grid>
            <Box>
              <DemandSecond
                partner={partner}
                date={date}
                targetDate={targetDate}
                config={config}
                target={target}
              />
            </Box>
          </>
        )} */}
        {tabs[demandTab] === 'third' && (
          <>
            <Grid size={12} sx={{ mt: '1em', mb: '1em' }}>
              <div id="demand" />
            </Grid>
            <Box>
              <DemandPlace
                partner={partner}
                date={date}
                targetDate={targetDate}
                config={config}
                scrollRef={scrollRef}
              />
            </Box>
          </>
        )}
      </Box>
    </>
  )
}
export const Dashboard2: React.FC<
  { partner: string; permissions: string[] } & ConfigProps & ScrollProps
> = ({ partner, permissions, config, scrollRef }) => {
  const translate = useTranslate()
  if (
    isNil(
      find(
        (item: string) =>
          item === 'owner' ||
          item === 'user' ||
          item === 'highManager' ||
          startsWith('manager')(item)
      )(permissions)
    )
  )
    return null
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 5,
          flexDirection: 'row',
          bgcolor: '#F9DD87',
          p: '0.5em'
        }}>
        <Box sx={{ display: 'flex', fontWidth: 'bold', color: 'primary.main' }}>
          <SquareIcon />
          <Typography variant="h6">
            {translate('resources.dashboard2.title')}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ mt: '0em' }}>
        <Dashboard2Content
          partner={partner}
          config={config}
          permissions={permissions}
          scrollRef={scrollRef}
        />
      </Box>
    </>
  )
}
// const FirstSelect: React.FC<{
//   partner: string
//   setTarget: (target: string) => void
//   areaKey: string
// }> = ({ partner, setTarget, areaKey }) => {
//   const { data, isLoading } = useGetMappings(partner)
//   const [choices, setChoices] = React.useState<string[]>([])
//   const [value, setValue] = React.useState<string>('')
//   const [open, setOpen] = React.useState<boolean>(false)
//   React.useEffect(() => {
//     if (isLoading || isNil(data)) return
//     const choices = sortBy<string>((value) => value)(
//       keys(groupBy<Mapping>(areaKey)(data))
//     )
//     setChoices(choices)
//     const value = head(choices) as string
//     setValue(value)
//     setTarget(value)
//   }, [isLoading, data])
//   const handleChange = (event: SelectChangeEvent): void => {
//     const value = event.target.value
//     setValue(value)
//     setTarget(value)
//     setOpen(false)
//   }
//   const handleClick = (): void => {
//     setOpen(true)
//   }
//   return (
//     <Box sx={{ display: 'flex', alignItems: 'center' }}>
//       <Button variant="contained" onClick={handleClick}>
//         {`${areaKey}選択`}
//       </Button>
//       <FormControl sx={{ ml: '0.5em' }}>
//         <Box
//           sx={{
//             display: 'flex',
//             alignItems: 'center',
//             border: 1,
//             borderRadius: 2
//           }}>
//           <IconButton
//             disabled={value === head(choices)}
//             onClick={(): void => {
//               const index = findIndex((item: string) => item === value)(choices)
//               if (index >= 0) {
//                 const value = choices[index - 1] as string
//                 setValue(value)
//                 setTarget(value)
//               }
//             }}>
//             <ArrowLeft />
//           </IconButton>
//           <Select
//             labelId="first-select-label"
//             IconComponent="none"
//             value={value}
//             onChange={handleChange}
//             renderValue={(value: string) => (
//               <Typography sx={{ mt: '-0.5em' }}>{value}</Typography>
//             )}
//             open={open}>
//             {map((item: string) => {
//               return <MenuItem value={item}>{item}</MenuItem>
//             })(choices)}
//           </Select>
//           <IconButton
//             disabled={value === last(choices)}
//             onClick={(): void => {
//               const index = findIndex((item: string) => item === value)(choices)
//               if (index >= 0) {
//                 const value = choices[index + 1] as string
//                 setValue(value)
//                 setTarget(value)
//               }
//             }}>
//             <ArrowRight />
//           </IconButton>
//         </Box>
//       </FormControl>
//     </Box>
//   )
// }
export const OrganizationSelect: React.FC<{
  partner: string
  setTarget: (target: string) => void
  organizationKeys: string[]
  index: number
  owner?: string | null | undefined
  filterKeys: string[]
}> = ({ partner, setTarget, organizationKeys, filterKeys, index, owner }) => {
  const { data, isLoading } = useGetMappings(partner)
  const { permissions, isLoading: isPermissionsLoading } = usePermissions()
  const { data: identityData, isLoading: isIdentityLoading } = useGetIdentity()
  const [choices, setChoices] = React.useState<string[]>([])
  const [value, setValue] = React.useState<string>('')
  const [open, setOpen] = React.useState<boolean>(false)
  const [filterValue, setFilterValue] = React.useState<
    Record<string, string> | undefined
  >(undefined)
  const organizationKey: string = organizationKeys[index] as string
  const areaKey: string = filterKeys[0] as string
  React.useEffect(() => {
    if (isPermissionsLoading || isIdentityLoading) return
    if (
      isNil(
        find(
          (item: string) => startsWith('manager')(item) && item !== 'manager#0'
        )(permissions)
      )
    )
      return
    if (!isNil(identityData)) {
      const {
        cognitoUser: {
          signInUserSession: {
            idToken: {
              payload: { 'custom:organizations': organizations }
            }
          }
        }
      } = identityData
      if (!isNil(organizations)) {
        const area = head(split(',')(organizations))
        if (!isNil(area)) {
          setFilterValue({ [areaKey]: area })
        }
      }
    }
  }, [
    isPermissionsLoading,
    isIdentityLoading,
    permissions,
    identityData,
    areaKey
  ])
  React.useEffect(() => {
    if (isLoading || isNil(data)) return
    const choices = sortBy<string>((value) => value)(
      keys(
        groupBy<Mapping>(organizationKey)(
          isNil(owner)
            ? isNil(filterValue)
              ? data
              : filter((item: Mapping) => {
                  const key = keys(filterValue)[0] as string
                  return item[key] === filterValue[key]
                })(data)
            : filter((item: Mapping) => item.ownedBy === owner)(data)
        )
      )
    )
    setChoices(choices)
    const value = head(choices) as string
    setValue(value)
    setTarget(value)
  }, [isLoading, data, organizationKey, index, filterValue, owner])
  const handleChange = (event: SelectChangeEvent): void => {
    const value = event.target.value
    setValue(value)
    setTarget(value)
    setOpen(false)
  }
  const handleClose = (): void => {
    setOpen(false)
  }
  const handleOpen = (): void => {
    setOpen(true)
  }
  if (isEmpty(choices)) return null
  if (isPermissionsLoading || isIdentityLoading) return null
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center'
      }}>
      <Button variant="contained" onClick={handleOpen} sx={{ flexGrow: 1 }}>
        {`${organizationKeys[index] as string}選択`}
      </Button>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          border: 1,
          borderRadius: 2,
          backgroundColor: '#FEF7E3',
          ml: '0.5em'
        }}>
        <IconButton
          disabled={value === head(choices)}
          onClick={(): void => {
            const index = findIndex((item: string) => item === value)(choices)
            if (index >= 0) {
              const value = choices[index - 1] as string
              setValue(value)
              setTarget(value)
            }
          }}>
          <ArrowLeft />
        </IconButton>
        <FormControl variant="standard" size="small">
          <Select
            labelId="first-select-label"
            // @ts-expect-error ts(2332)
            IconComponent="none"
            value={value}
            onChange={handleChange}
            renderValue={(value: string) => (
              <Typography sx={{}}>{value}</Typography>
            )}
            onClose={handleClose}
            onOpen={handleOpen}
            open={open}>
            {map((item: string) => {
              return (
                <MenuItem value={item} key={item}>
                  {item}
                </MenuItem>
              )
            })(choices)}
          </Select>
        </FormControl>
        <IconButton
          disabled={value === last(choices)}
          onClick={(): void => {
            const index = findIndex((item: string) => item === value)(choices)
            if (index >= 0) {
              const value = choices[index + 1] as string
              setValue(value)
              setTarget(value)
            }
          }}>
          <ArrowRight />
        </IconButton>
      </Box>
    </Box>
  )
}
